import catalogApi from "../../services/catalogs";
import searchApi from "../../services/search";
import articleApi from "../../services/article";
import _ from "lodash";
import catalogMappingFieldsApi from "../../services/catalogMappingFields";
import catalogMappingApi from "../../services/catalogMapping";

const defaultState = () => ({
  runningPromises: [],
  mappingTargets: [],
  catalogs: [],

  modified: {
    catalogDetails: false,
    catalogMappings: false,
    baseMapping: false
  },
  updateTasks: {
    catalogDetails: [],
    catalogMappings: [],
    baseMapping: []
  },
  updateErrors: {
    catalogDetails: [],
    catalogMappings: [],
    baseMapping: []
  },
  metadata: {
    loading: false,
    update: {
      seen: true
    }
  },
  selectedTargetCatalogId: null,
  targetMappingNodes: [],
  fieldMappings: new Map(),
  toBeDeletedFieldMappingMap: new Map(),
  sourceCatalogSearch: {
    keyword: "",
    results: []
  },
  targetCatalogSearch: {
    keyword: "",
    results: []
  },
  attributGroups: [],
  mappingProjects: [],
  selectedAttributeGroup: undefined,
  selectedMappingProjectId: "",
  shouldReloadSourceFieldLocalizations: false,
  isSourceFieldsTreeBusy: false,
  isTargetMappingTreeBusy: false,
  targetAbortController: null,
  sourceAbortController: null,
  articleStatuses: [],
  isLoading: false,
  isLoaded: false,
  isSourceCatalogStructureLoaded: false,
  isTargetCatalogStructureLoaded: false
});

export default {
  namespaced: true,
  state: { ...defaultState() },
  getters: {
    isSaving(state) {
      return Object.keys(state.updateTasks).some((x) => !!state.updateTasks[x].length);
    },
    hasUnsavedData(state) {
      const sections = Object.keys(state.modified);
      return sections.some((x) => state.modified[x]);
    },
    lastRunSuccess(state) {
      return Object.keys(state.updateErrors).every((x) => !state.updateErrors[x].length);
    },
    isBaseMappingTabBusy: function (state) {
      return !!state.runningPromises.length || state.isSourceFieldsTreeBusy || state.isTargetMappingTreeBusy;
    },
    selectedTargetCatalog(state, getters, rootState) {
      const lang = rootState.metadata.contentLanguages.enabled[0]?.languageCode;
      if (lang) {
        return state.catalogs.find((c) => c[lang]?.catalogId == state.selectedTargetCatalogId);
      } else {
        return null;
      }
    },
    selectedMappingProject(state) {
      return state.mappingProjects.find((m) => m.catalogMappingProjectId == state.selectedMappingProjectId);
    },
    isChannelCreatorLoaded(state) {
      return state.isSourceCatalogStructureLoaded && state.isTargetCatalogStructureLoaded;
    }
  },
  mutations: {
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    "add-running-promise": function (state, promise) {
      state.runningPromises.push(promise);
    },
    "reset-running-promises": function (state) {
      state.runningPromises = [];
    },
    "set-is-source-fields-tree-busy": function (state, args) {
      state.isSourceFieldsTreeBusy = args;
    },
    "set-is-target-mapping-tree-busy": function (state, args) {
      state.isTargetMappingTreeBusy = args;
    },
    setArticleStatuses(state, statuses) {
      state.articleStatuses = statuses;
    },
    setMappingTargets(state, mappingTargets) {
      state.mappingTargets = mappingTargets;
    },
    setCatalogs(state, catalogs) {
      state.catalogs = catalogs;
    },
    addCatalog(state, catalog) {
      state.catalogs.push(catalog);
    },
    renameCatalog(state, args) {
      const statecatalogs = state.catalogs;
      const languages = Object.keys(args.catalog);
      if (languages.length) {
        const catalogId = args.catalog[languages[0]].catalogId;
        const index = state.catalogs.findIndex((x) => x[languages[0]].catalogId == catalogId);
        if (index >= 0) {
          statecatalogs.splice(index, 1, args.catalog);
        }

        const basicCatalog = statecatalogs.filter((x) => x[args.primarylanguage.languageCode].catalogType == 0);
        let catalogList = statecatalogs.filter((x) => x[args.primarylanguage.languageCode].catalogType != 0);

        const sortKeyFinder = (x) =>
          x[args.primarylanguage.languageCode]?.name?.toUpperCase() || x[args.primarylanguage.fallbackLanguageCode]?.name?.toUpperCase();
        catalogList = _.sortBy(catalogList, sortKeyFinder);
        if (basicCatalog && basicCatalog.length > 0) {
          catalogList.splice(0, 0, basicCatalog[0]);
        }

        state.catalogs = catalogList;
      }
    },
    deleteCatalog(state, catalog) {
      const languages = Object.keys(catalog);
      if (languages.length) {
        const catalogId = catalog[languages[0]].catalogId;
        const index = state.catalogs.findIndex((x) => x[languages[0]].catalogId == catalogId);
        if (index >= 0) {
          state.catalogs.splice(index, 1);
        }
      }
    },
    updateTargetCatalog(state, args) {
      state.selectedTargetCatalogId = args.catalogId;
    },
    markDirty(state, args) {
      state.modified[args.section] = args.dirty;
    },
    clearDirtyStatus(state) {
      state.modified = {
        catalogDetails: false,
        catalogMappings: false,
        baseMapping: false
      };
    },
    updateSaveStatus(state, arg) {
      const section = state.updateTasks[arg.section];
      if (arg.running && section.indexOf(arg.lang) < 0) section.push(arg.lang);
      if (!arg.running) {
        const x = section.indexOf(arg.lang);
        section.splice(x, 1);
      }
      state.metadata.update.seen = arg.seen == undefined ? false : arg.seen;

      if (arg.error) {
        state.updateErrors[arg.section].push(arg.error);
      }
    },
    resetUpdateStatus(state) {
      state.updateTasks = {
        catalogDetails: [],
        catalogMappings: [],
        baseMapping: []
      };
      state.updateErrors = {
        catalogDetails: [],
        catalogMappings: [],
        baseMapping: []
      };
    },
    setNotificationSeen(state) {
      state.metadata.update.seen = true;
    },
    sourceCatalogSearchResults(state, args) {
      state.sourceCatalogSearch = { ...args, current: 0 };
    },
    targetCatalogSearchResults(state, args) {
      state.targetCatalogSearch = { ...args, current: 0 };
    },
    changeCurrentSourceCatalogSearchResult(state, position) {
      state.sourceCatalogSearch.current = position;
    },
    changeCurrentTargetCatalogSearchResult(state, position) {
      state.targetCatalogSearch.current = position;
    },
    clearAllSearch(state, args) {
      if (!args || args.sourceCatalog) {
        state.sourceCatalogSearch = {
          keyword: "",
          results: [],
          current: 0
        };
      }

      if (!args || args.targetCatalog) {
        state.targetCatalogSearch = {
          keyword: "",
          results: [],
          current: 0
        };
      }
    },
    updateTargetFieldMappings(state, args) {
      state.targetMappingNodes = args;
    },
    updateFieldMappings(state, mappings) {
      state.fieldMappings = mappings;
    },
    deleteCurrentMapping(state, catalogMappingTargetNodeIds) {
      catalogMappingTargetNodeIds.forEach((catalogMappingTargetNodeId) => {
        const fieldmapping = state.fieldMappings.get(catalogMappingTargetNodeId);
        if (!fieldmapping.isNew) {
          state.toBeDeletedFieldMappingMap.set(catalogMappingTargetNodeId, fieldmapping);
        }
        state.fieldMappings.delete(catalogMappingTargetNodeId);
      });
    },
    "reset-ToBeDeletedFieldMappingMap"(state) {
      state.toBeDeletedFieldMappingMap = new Map();
    },
    addCurrentFieldMapping(state, mappingInfo) {
      const mapping = state.toBeDeletedFieldMappingMap.get(mappingInfo.catalogMappingTargetNodeId);
      if (mapping) {
        mapping.isNew = false;
        mapping.isUpdated = true;
        mappingInfo.fieldMapping.catalogFieldMappingId = mapping.currentMapping.catalogFieldMappingId;
        mapping.currentMapping = mappingInfo.fieldMapping;
        state.fieldMappings.set(mappingInfo.catalogMappingTargetNodeId, mapping);
        state.toBeDeletedFieldMappingMap.delete(mappingInfo.catalogMappingTargetNodeId);
      } else {
        state.fieldMappings.set(mappingInfo.catalogMappingTargetNodeId, {
          isNew: true,
          globalMapping: null,
          currentMapping: mappingInfo.fieldMapping
        });
      }
    },
    updateFieldMappingExpression(state, mappingExpressionInfo) {
      const fieldMapping = state.fieldMappings.get(mappingExpressionInfo.catalogMappingTargetNodeId);
      const currentMapping = fieldMapping?.currentMapping;
      if (currentMapping && (currentMapping.sourceExpression != undefined || currentMapping.sourceExpression != null)) {
        fieldMapping.isUpdated = true;
        currentMapping.sourceExpression = mappingExpressionInfo.sourceExpression;
      }
    },
    loadAttributeGroups(state, attributeGroups) {
      state.attributGroups = attributeGroups;
    },
    loadMappingProjects(state, mappingProjects) {
      state.mappingProjects = mappingProjects;
    },
    addMappingProject(state, mappingProject) {
      let mappingProjectList = state.mappingProjects;
      mappingProjectList.push(mappingProject);
      const sortKeyFinder = (x) => x?.name?.toUpperCase();
      mappingProjectList = _.sortBy(mappingProjectList, sortKeyFinder);
      state.mappingProjects = mappingProjectList;
    },
    updateSelectedMappingProjectId(state, mappingProjectId) {
      state.selectedMappingProjectId = mappingProjectId;
    },
    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setLoaded(state, isLoaded) {
      state.isLoaded = isLoaded;
    },
    updateSelectedAttributeGroup(state, attributeGroup) {
      state.selectedAttributeGroup = attributeGroup;
    },
    deleteMappingProject(state, mappingProjectId) {
      const index = state.mappingProjects.findIndex((x) => x.catalogMappingProjectId == mappingProjectId);
      if (index >= 0) {
        state.mappingProjects.splice(index, 1);
      }
    },
    updateMappingProjectName(state, mappingProject) {
      let mappingProjectList = state.mappingProjects;
      const index = mappingProjectList.findIndex((x) => x.catalogMappingProjectId == mappingProject.catalogMappingProjectId);
      if (index >= 0) {
        mappingProjectList.splice(index, 1, mappingProject);
      }
      const sortKeyFinder = (x) => x?.name?.toUpperCase();
      mappingProjectList = _.sortBy(mappingProjectList, sortKeyFinder);
      state.mappingProjects = mappingProjectList;
    },
    setShouldReloadSourceFieldLocalizations(state, shouldReloadSourceFieldLocalizations) {
      state.shouldReloadSourceFieldLocalizations = shouldReloadSourceFieldLocalizations;
    },
    setSourceAbortController(state, args) {
      state.sourceAbortController = args;
    },
    setTargetAbortController(state, args) {
      state.targetAbortController = args;
    },
    setIsSourceCatalogStructureLoaded(state, args) {
      state.isSourceCatalogStructureLoaded = args;
    },
    setIsTargetCatalogStructureLoaded(state, args) {
      state.isTargetCatalogStructureLoaded = args;
    }
  },
  actions: {
    resetRunningPromisesIfResolved: async function ({ state, commit }) {
      await Promise.all(state.runningPromises);
      commit("reset-running-promises");
    },
    addRunningPromise: async function ({ commit }, promise) {
      commit("add-running-promise", promise);
    },
    setIsSourceFieldsTreeBusy: function ({ commit }, args) {
      commit("set-is-source-fields-tree-busy", args);
    },
    setIsTargetMappingTreeBusy: function ({ commit }, args) {
      commit("set-is-target-mapping-tree-busy", args);
    },
    loadMappingTargets: async function (context) {
      const mappingTargets = await catalogApi.getMappingTargets({
        catalogType: 1
      });
      context.commit("setMappingTargets", mappingTargets);
    },
    loadAllStatuses: async function (context) {
      if (context.state.articleStatuses.length > 0) return;
      const statuses = await articleApi.getAllArticleStates();
      context.commit("setArticleStatuses", statuses);
    },
    loadCatalogs: async function ({ commit, rootState }, args) {
      commit("setLoading", true);
      commit("resetUpdateStatus");
      args = args || {};
      const requestedLanguages = args.languages || rootState.metadata.contentLanguages.enabled;
      let languages = requestedLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = Array.from(new Set(languages));

      const catalogs = {};
      for (let index = 0; index < languages.length; index++) {
        const lang = languages[index];
        const allCatalogs = await catalogApi.getCatalogs({
          language: lang,
          catalogTypes: [0, 1]
        });

        allCatalogs.forEach((item) => {
          const cmodel = catalogs[item.catalogId] || {};
          cmodel[lang] = item;
          catalogs[item.catalogId] = cmodel;
        });
      }

      const primaryLanguage = rootState.metadata.contentLanguages.enabled[0];
      let catalogList = Object.values(catalogs);
      const sortKeyFinder = (x) => x[primaryLanguage.languageCode]?.name?.toUpperCase() || x[primaryLanguage.fallbackLanguageCode]?.name?.toUpperCase();
      catalogList = _.sortBy(catalogList, sortKeyFinder);
      commit("setCatalogs", catalogList);
      commit("setLoading", false);
    },
    addCatalog: async function (context, catalog) {
      const primarylanguage = context.rootState.metadata.contentLanguages.enabled[0];
      const model = catalog[primarylanguage.languageCode];
      const newcatalog = await catalogApi.create(
        model.name,
        model.catalogType,
        model.category,
        model.isLinkable,
        primarylanguage.languageCode,
        model.catalogMappingTargetId,
        model.catalogUid || null
      );

      const languages = context.rootState.metadata.contentLanguages.enabled;
      languages.forEach((lang) => {
        catalog[lang.languageCode].catalogId = newcatalog.catalogId;
        catalog[lang.languageCode].createdOn = newcatalog.createdOn;
        catalog[lang.languageCode].createdBy = newcatalog.createdBy;
        catalog[lang.languageCode].updatedOn = newcatalog.updatedOn;
        catalog[lang.languageCode].updatedBy = newcatalog.updatedBy;
        catalog[lang.languageCode].accessRights = newcatalog.accessRights ?? {
          ...lang.accessRights
        };
      });

      const basicCatalog = context.state.catalogs.filter((x) => x[primarylanguage.languageCode].catalogType == 0);
      let catalogList = context.state.catalogs.filter((x) => x[primarylanguage.languageCode].catalogType != 0);
      catalogList.push(catalog);
      const sortKeyFinder = (x) => x[primarylanguage.languageCode]?.name?.toUpperCase() || x[primarylanguage.fallbackLanguageCode]?.name?.toUpperCase();
      catalogList = _.sortBy(catalogList, sortKeyFinder);
      if (basicCatalog && basicCatalog.length > 0) {
        catalogList.splice(0, 0, basicCatalog[0]);
      }

      context.commit("setCatalogs", catalogList);
      return newcatalog.catalogId;
    },
    renameCatalog: async function ({ commit, rootState }, catalog) {
      const primarylanguage = rootState.metadata.contentLanguages.enabled[0];
      const tasks = Object.keys(catalog).map((lang) =>
        catalogApi.renameCatalog(catalog[lang].catalogId, catalog[lang].name || "", lang, catalog[lang].category || "")
      );
      await Promise.all(tasks);

      commit("renameCatalog", { catalog, primarylanguage });
    },
    removeCatalog: async function ({ commit, rootState }, catalog) {
      const primarylanguage = rootState.metadata.contentLanguages.enabled[0];
      const model = catalog[primarylanguage.languageCode];
      await catalogApi.deleteCatalog(model.catalogId);

      commit("deleteCatalog", catalog);
    },
    updateTargetCatalog({ commit, rootState }, newTargetCatalogId) {
      const primarylanguage = rootState.metadata.contentLanguages.enabled[0].languageCode;

      commit("setLoading", true);
      commit("updateTargetCatalog", {
        catalogId: newTargetCatalogId,
        language: primarylanguage
      });
      // No need of loading source fields and target mappings when no attribute group or mapping project is selected
      commit("set-is-source-fields-tree-busy", !!this.selectedAttributeGroup);
      commit("set-is-target-mapping-tree-busy", !!this.selectedMappingProjectId);
      commit("setLoading", false);
    },

    saveCatalogDetails: async function (context, args) {
      const languages = context.rootState.metadata.contentLanguages.enabled;
      context.commit("updateSaveStatus", {
        section: "catalogDetails",
        lang: languages[0].languageCode,
        running: true
      });
      try {
        await catalogApi.updateCatalogSettings(args.catalogDetails);
        for (let index = 0; index < languages.length; index++) {
          const language = languages[index];
          await catalogApi.updateCatalogAttributes(args.catalogDetails.catalogId, language.languageCode, args.catalogAttributes[language.languageCode]);
        }
        context.commit("updateSaveStatus", {
          section: "catalogDetails",
          lang: languages[0].languageCode,
          running: false
        });
      } catch (error) {
        context.commit("updateSaveStatus", {
          section: "catalogDetails",
          lang: languages[0].languageCode,
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
    },
    searchSourceCatalog: async function (context, args) {
      try {
        if (args.keyword == context.state.sourceCatalogSearch.keyword && !args.force) {
          if (context.state.sourceCatalogSearch.results.length > context.state.sourceCatalogSearch.current + 1) {
            context.commit("changeCurrentSourceCatalogSearchResult", context.state.sourceCatalogSearch.current + 1);
          }
          return;
        }
        const results = await searchApi.searchInCatalog(args.catalogId, args.keyword, args.language);
        context.commit("sourceCatalogSearchResults", {
          keyword: args.keyword,
          results
        });
      } catch (error) {
        console.error("failed searching source catalog", error);
      }
    },
    searchTargetCatalog: async function (context, args) {
      try {
        if (args.keyword == context.state.targetCatalogSearch.keyword && !args.force) {
          if (context.state.targetCatalogSearch.results.length > context.state.targetCatalogSearch.current + 1) {
            context.commit("changeCurrentTargetCatalogSearchResult", context.state.targetCatalogSearch.current + 1);
          }
          return;
        }
        const results = await searchApi.searchInCatalog(args.catalogId, args.keyword, args.language);
        context.commit("targetCatalogSearchResults", {
          keyword: args.keyword,
          results
        });
      } catch (error) {
        console.error("failed searching source catalog", error);
      }
    },
    cancelSourceActionsIfAny: async function ({ state }) {
      if (state.sourceAbortController) {
        state.sourceAbortController.abort();
      }
      state.sourceAbortController = null;
    },
    cancelTargetActionsIfAny: async function ({ state }) {
      if (state.targetAbortController) {
        state.targetAbortController.abort();
      }
      state.targetAbortController = null;
    },
    loadFieldMappings: async function (context, args) {
      try {
        if (!args.catalogMappingProjectId || !args.catalogMappingId) {
          context.commit("updateFieldMappings", new Map());
        } else {
          const promise = catalogMappingFieldsApi.get(
            args.catalogMappingProjectId,
            args.catalogMappingId,
            args.isReferential,
            context.state.targetAbortController?.signal
          );
          context.commit("add-running-promise", promise);
          const mappings = await promise;
          const fieldMappingMap = mappings.reduce((fieldMappingMap, item) => {
            const group = fieldMappingMap.get(item.targetFieldId) || {
              globalMapping: null,
              currentMapping: null
            };
            if (item.isInherited === true) {
              group.globalMapping = item;
            } else {
              group.currentMapping = item;
            }
            fieldMappingMap.set(item.targetFieldId, group);
            return fieldMappingMap;
          }, new Map());

          context.commit("updateFieldMappings", fieldMappingMap);

          await context.dispatch("resetRunningPromisesIfResolved");
        }
      } catch (error) {
        console.error(error);
      }
    },
    createFieldMapping({ commit }, args) {
      try {
        commit("markDirty", { section: "baseMapping", dirty: true });
        let targetNodeName = replaceInvalidChars(args.targetNodeName);

        const existingTargetNodeNames = args.existingTargetNodeNames ?? [];
        if (_.includes(existingTargetNodeNames, targetNodeName)) {
          targetNodeName = GetSequentialName(existingTargetNodeNames, targetNodeName);
        }
        const fieldMapping = {
          catalogFieldMappingId: generateRandomId(),
          catalogMappingId: args.catalogMappingId,
          targetFieldId: args.catalogMappingTargetNodeId,
          sourceExpression: args.sourceExpression,
          targetNodeName: targetNodeName
        };
        commit("addCurrentFieldMapping", {
          catalogMappingTargetNodeId: args.catalogMappingTargetNodeId,
          fieldMapping: fieldMapping
        });
        return targetNodeName;
      } catch (error) {
        console.error(`Error on creating mapping for target field "${args.targetNodeName}"`);
      }
    },

    updateFieldMapping(context, args) {
      try {
        context.commit("updateFieldMappingExpression", {
          catalogMappingTargetNodeId: args.catalogMappingTargetNodeId,
          sourceExpression: args.sourceExpression
        });

        context.commit("markDirty", { section: "baseMapping", dirty: true });
      } catch (error) {
        console.error("Error on updating mapping for target field " + args.catalogFieldMappingId);
      }
    },
    deleteFieldMappings(context, args) {
      try {
        context.commit("deleteCurrentMapping", args.catalogMappingTargetNodeIds);
        context.commit("markDirty", { section: "baseMapping", dirty: true });
      } catch (error) {
        console.error("Error on deleting selected catalog field mappings.");
      }
    },
    deleteAllFieldMappings: async function (context, args) {
      try {
        await catalogMappingFieldsApi.deleteAll(args.catalogMappingProjectId, args.attributeGroupId);
        context.commit("deleteCurrentMapping", args.catalogMappingTargetNodeIds);
        context.commit("reset-ToBeDeletedFieldMappingMap");
        context.commit("markDirty", { section: "baseMapping", dirty: false });
      } catch (error) {
        console.error("Error on deleting field mappings of the selected attribute group.");
      }
    },
    saveFieldMapping: async function ({ state, commit, rootState }) {
      const languages = rootState.metadata.contentLanguages.enabled;
      try {
        commit("updateSaveStatus", {
          section: "baseMapping",
          lang: languages[0].languageCode,
          running: true
        });
        const fieldMappingValues = state.fieldMappings.values();
        const toBeAddedList = [];
        const toBeUpdatedList = [];
        for (const mapping of fieldMappingValues) {
          if (mapping.isNew) {
            mapping.currentMapping.catalogFieldMappingId = null;
            toBeAddedList.push(mapping.currentMapping);
          }
          if (mapping.isUpdated) toBeUpdatedList.push(mapping.currentMapping);
        }

        if (toBeAddedList.length) {
          const createdMappings = await catalogMappingFieldsApi.create(state.selectedMappingProjectId, toBeAddedList);
          for (const mapping of createdMappings) {
            const fieldMapping = state.fieldMappings.get(mapping.catalogMappingTargetNodeId);
            fieldMapping.isNew = false;
            fieldMapping.currentMapping.catalogFieldMappingId = mapping.catalogFieldMappingId;
          }
        }

        if (toBeUpdatedList.length) {
          await catalogMappingFieldsApi.update(toBeUpdatedList);
          for (const entry of toBeUpdatedList) {
            entry.isUpdated = false;
          }
        }

        if (state.toBeDeletedFieldMappingMap.size) {
          const array = Array.from(state.toBeDeletedFieldMappingMap.values());
          const list = array.map((x) => x.currentMapping.catalogFieldMappingId);
          await catalogMappingFieldsApi.delete(list);
          commit("reset-ToBeDeletedFieldMappingMap");
        }

        commit("updateSaveStatus", {
          section: "baseMapping",
          lang: languages[0].languageCode,
          running: false
        });
      } catch (error) {
        commit("updateSaveStatus", {
          section: "baseMapping",
          lang: languages[0].languageCode,
          running: false,
          error: error,
          apiError: error.isAxiosError,
          success: false
        });
      }
    },
    loadAttributeGroupsOfCatalog: async function (context, args) {
      if (!args.catalogId || !args.catalogMappingProjectId) {
        context.commit("loadAttributeGroups", []);
      } else {
        try {
          context.commit("set-is-source-fields-tree-busy", true);
          const promise = catalogMappingApi.get(args.catalogId, args.catalogMappingProjectId, args.languageCode, context.state.targetAbortController?.signal);
          context.commit("add-running-promise", promise);
          const mappingAttributeGroups = await promise;
          const attributGroupList = mappingAttributeGroups.map((x) => ({
            id: x.attributeGroupId,
            catalogMappingId: x.catalogMappingId,
            path: x.attributeGroupNamePath,
            sequence: x.attributeGroupOrder,
            isReferential: x.isReferential,
            isMapped: x.isMapped
          }));
          context.commit("loadAttributeGroups", attributGroupList);
          const attributeGroup = context.state.attributGroups?.length > 0 ? context.state.attributGroups[0] : null;
          context.commit("updateSelectedAttributeGroup", attributeGroup);
          await context.dispatch("resetRunningPromisesIfResolved");
        } catch (error) {
          console.error("Loading of attribute groups failed.");
        }
      }
    },
    loadMappingProjectsOfCatalog: async function (context, args) {
      if (context.state.isLoading || context.state.isLoaded) return;
      if (!args.catalogId) {
        context.commit("loadMappingProjects", []);
      } else {
        try {
          context.commit("set-is-target-mapping-tree-busy", true);
          context.commit("setLoading", true);
          const promise = catalogApi.getCatalogMappingProjects(args.catalogId, args.languageCode, context.state.targetAbortController?.signal);
          context.commit("add-running-promise", promise);
          const targetMappingProjects = await promise;
          let mappingProjectList = targetMappingProjects
            .map((x) =>
              x.mappingProjects.map((y) => ({
                catalogMappingProjectId: y.catalogMappingProjectId,
                catalogMappingTargetId: x.catalogMappingTargetId,
                name: y.name + " (" + x.targetSystemName + ")",
                targetSystemName: x.targetSystemName,
                targetSystem: x.targetSystem
              }))
            )
            .flat(1);
          const sortKeyFinder = (x) => x?.name?.toUpperCase();
          mappingProjectList = _.sortBy(mappingProjectList, sortKeyFinder);
          context.commit("loadMappingProjects", mappingProjectList);
          const mappingProjectId = context.state.mappingProjects?.length > 0 ? context.state.mappingProjects[0].catalogMappingProjectId : "";
          context.commit("updateSelectedMappingProjectId", mappingProjectId);
          await context.dispatch("resetRunningPromisesIfResolved");
          context.commit("setLoaded", true);
        } catch (error) {
          console.error("Loading of catalog mapping projects failed.");
        } finally {
          context.commit("setLoading", false);
        }
      }
    },
    updateSelectedMappingProjectId(context, mappingProjectId) {
      context.commit("updateSelectedMappingProjectId", mappingProjectId);
      context.commit("set-is-source-fields-tree-busy", !!mappingProjectId); // No need of loading source fields when no mapping project is selected
    },
    updateSelectedAttributeGroup(context, attributeGroup) {
      context.commit("updateSelectedAttributeGroup", attributeGroup);
      context.commit("set-is-source-fields-tree-busy", !!attributeGroup); // No need of loading source fields when no attribute group is selected
    },
    addMappingProject: async function (context, args) {
      try {
        const newCatalogMappingProject = await catalogApi.createCatalogMappingProject(
          args.catalogMappingProjectName,
          args.catalogId,
          args.catalogMappingTargetId
        );
        const newMappingProject = {
          catalogMappingProjectId: newCatalogMappingProject.catalogMappingProjectId,
          catalogMappingTargetId: newCatalogMappingProject.catalogMappingTargetId,
          name: newCatalogMappingProject.name + " (" + args.catalogMappingTargetName + ")",
          targetSystemName: args.catalogMappingTargetName,
          targetSystem: newCatalogMappingProject.targetSystem
        };

        context.commit("addMappingProject", newMappingProject);

        return newCatalogMappingProject.catalogMappingProjectId;
      } catch (error) {
        console.error("Creation of catalog mapping project with name " + args.catalogMappingProjectName + " failed.");
      }
    },
    copyMappingProject: async function (context, args) {
      try {
        const newCatalogMappingProject = await catalogApi.copyMappingProject(args.catalogId, args.catalogMappingProjectId, args.languageCode);
        const newMappingProject = {
          catalogMappingProjectId: newCatalogMappingProject.catalogMappingProjectId,
          catalogMappingTargetId: args.catalogMappingTargetId,
          name: newCatalogMappingProject.name + " (" + args.targetSystemName + ")",
          targetSystemName: args.targetSystemName,
          targetSystem: args.targetSystem
        };

        context.commit("addMappingProject", newMappingProject);

        return newCatalogMappingProject.catalogMappingProjectId;
      } catch (error) {
        console.error("Copying of catalog mapping project " + args.catalogMappingProjectId + " failed.");
      }
    },
    deleteMappingProject: async function (context, catalogMappingProjectId) {
      try {
        await catalogApi.deleteMappingProject(catalogMappingProjectId);
        context.commit("deleteMappingProject", catalogMappingProjectId);
      } catch (error) {
        console.error("Deletion of catalog mapping project " + catalogMappingProjectId + " failed.");
      }
    },
    updateMappingProjectName: async function (context, args) {
      try {
        await catalogApi.renameMappingProject(args.mappingProject.name, args.catalogId, args.mappingProject.catalogMappingProjectId);
        args.mappingProject.name = args.mappingProject.name + " (" + args.mappingProject.targetSystemName + ")";

        context.commit("updateMappingProjectName", args.mappingProject);
        return true;
      } catch (error) {
        console.error("Rename of catalog mapping project with name " + args.mappingProject.name + " failed.");
        if (error.isAxiosError) {
          return error.response.data?.error;
        }
      }
    }
  }
};
//#region Utility
const replaceInvalidChars = (text) => {
  if (!text) return text;

  const invalidChars = [".", "!", "`", "[", "]"];

  for (let i = 0; i < 31; i++) invalidChars.push(String.fromCharCode(i));
  for (const char of invalidChars) {
    text = text.replaceAll(char, "");
  }
  return text;
};

const GetSequentialName = (nodeNames, name) => {
  const possibleNumbers = _.map(nodeNames, (node) => parseInt(ExtractNumber(node, name)));
  const useNodeIndex = _.max([1, _.max(possibleNumbers) + 1]);

  return `${name} (${useNodeIndex})`;
};

const ExtractNumber = (input, name) => {
  const escapedName = _.escapeRegExp(name);
  const regex = new RegExp(`(${escapedName} [(])(\\d+)([)])$`, "g");
  const matchGroups = [...input.matchAll(regex)];
  if (!matchGroups.length) return -1;
  return matchGroups[0].length == 4 ? matchGroups[0][2] : -1;
};

const generateRandomId = function () {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz";
  const lenString = 7;
  let randomstring = "";

  for (let i = 0; i < lenString; i++) {
    const rnum = Math.floor(Math.random() * characters.length);
    randomstring += characters.substring(rnum, rnum + 1);
  }

  return randomstring;
};
//#endregion
